import { Stepper } from '@mantine/core';
import { IconFileText, IconHome, IconSitemap, IconUserPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OnboardingAssetInfoForm } from 'src/Components/Forms/OnboardingAssetInfoForm';
import { OnboardingInviteUsersForm } from 'src/Components/Forms/OnboardingInviteUsersForm';
import { OnboardingSelectRealEstatePlanForm } from 'src/Components/Forms/OnboardingSelectRealEstatePlanForm';
import { OnboardingTypeSelectForm } from 'src/Components/Forms/OnboardingTypeSelectForm';
import { FullScreenFormWrapper } from 'src/Containers/FullScreenFormWrapper';
import useUpdateLastAssetDetailSelectionDate from 'src/Hooks/Mutations/useUpdateLastAssetDetailSelectionDate';
import useUpdateLastAssetNameCompletionDate from 'src/Hooks/Mutations/useUpdateLastAssetNameCompletionDate';
import useUpdateLastAssetTypeSelectionDate from 'src/Hooks/Mutations/useUpdateLastAssetTypeSelectionDate';
import { useFetchAssets } from 'src/Hooks/Queries/useFetchAssets';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { CreateAssetDtoSubTypeEnum } from 'src/Services/API/generated';

import styles from './OnboardingPage.module.scss';
import { Props } from './OnboardingPage.types';

const OnboardingPage = ({ isFirstAsset }: Props) => {
  const [active, setActive] = useState(0);
  const [assetId, setAssetId] = useState<string | undefined>(undefined);
  const [subType, setSubType] = useState<CreateAssetDtoSubTypeEnum | undefined>(undefined);
  const previousStep = () => setActive(current => (current > 0 ? current - 1 : current));
  const setNextStep = useCallback(
    (assetId: string) => {
      setActive(3);
      setAssetId(assetId);
    },
    [setActive]
  );
  const { t } = useTranslation();
  const { data: assets } = useFetchAssets();

  const firstAssetCreated = isFirstAsset && assets && assets.length > 0;

  // If isFirstAsset is set it means we are in the onboarding for the first asset of the user
  // useEffect below is used when we come back to the onboarding after asset creation (if onboarding was stopped)

  useEffect(() => {
    if (firstAssetCreated) {
      setActive(3); // share step
    }
  }, [firstAssetCreated]);

  useEffect(() => {
    if (!assetId && isFirstAsset && assets && assets.length > 0) {
      setAssetId(assets[0].id);
    }
  }, [assetId, assets, isFirstAsset]);

  const { mutate: pingOnTypeSelect } = useUpdateLastAssetTypeSelectionDate();
  const { mutate: pingOnNameComplete } = useUpdateLastAssetNameCompletionDate();
  const { mutate: pingOnDetailSelect } = useUpdateLastAssetDetailSelectionDate();

  const isMobile = useIsMobile(1000);

  return (
    <FullScreenFormWrapper maxWidth={1100}>
      <Stepper
        active={active}
        onStepClick={setActive}
        className={styles.stepper}
        allowNextStepsSelect={false}
      >
        <Stepper.Step
          allowStepSelect={false}
          label={
            !isMobile &&
            (isFirstAsset
              ? t('onboarding.firstAsset.title', 'Premier bien')
              : t('onboarding.newAsset.title', 'Nouveau bien'))
          }
          description={
            !isMobile && t('onboarding.firstAsset.subtitle', 'Sélectionnez le type de votre bien')
          }
          icon={<IconHome size={22} />}
        >
          <OnboardingTypeSelectForm
            isFirstAsset={isFirstAsset}
            onTypeSelect={() => {
              setActive(1);
              pingOnTypeSelect();
            }}
          />
        </Stepper.Step>
        <Stepper.Step
          allowStepSelect={false}
          label={!isMobile && t('onboarding.pricing.title', 'Usage du bien')}
          description={
            !isMobile && t('onboarding.pricing.subtitle', "Sélectionnez le type d'occupation")
          }
          icon={<IconSitemap size={22} />}
        >
          <OnboardingSelectRealEstatePlanForm
            onPlanSelect={selectedSubType => {
              setSubType(selectedSubType);
              setActive(2);
              pingOnDetailSelect();
            }}
            goToPreviousStep={previousStep}
          />
        </Stepper.Step>
        <Stepper.Step
          allowStepSelect={false}
          label={!isMobile && t('onboarding.details.title', 'Détails')}
          description={
            !isMobile && t('onboarding.details.subtitle', 'Ajoutez les détails liés à votre bien')
          }
          icon={<IconFileText size={22} />}
        >
          <OnboardingAssetInfoForm
            subType={subType}
            onCreate={assetId => {
              setNextStep(assetId);
              pingOnNameComplete();
            }}
            goToPreviousStep={previousStep}
          />
        </Stepper.Step>
        <Stepper.Step
          allowStepSelect={false}
          label={!isMobile && t('onboarding.members.title', 'Membres')}
          description={!isMobile && t('onboarding.members.subtitle', 'Invitez des membres')}
          icon={<IconUserPlus size={22} />}
        >
          {assetId && <OnboardingInviteUsersForm assetId={assetId} />}
        </Stepper.Step>
      </Stepper>
    </FullScreenFormWrapper>
  );
};

export default OnboardingPage;
